import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AddBoxIcon from '@mui/icons-material/AddBox'
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox'
import MedicationLiquidIcon from '@mui/icons-material/MedicationLiquid'
import AnDateTimePicker from '../../../../components/DateTime/DateTimePicker'
import EmployeeSelector from '../../../events/EmployeeSelector'

const AdministrationDetailsCard = ({
  canReuse,
  inEpcr,
  amountMLs,
  setAmountMLs,
  stockRemaining,
  setStockRemaining,
  selectedDrugsAdministered,
  handleQTYchange,
  totalDose,
  setTotalDose,
  totalDoseType,
  setTotalDoseType,
  route,
  setRoute,
  administeredBy,
  setAdministeredBy,
  administeredTime,
  setAdministeredTime,
}) => {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <MedicationLiquidIcon color="primary" />
          <Box>
            <Typography variant="h6" fontWeight={800}>
              Administration Details
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Record the quantity, dose, route and clinician.
            </Typography>
          </Box>
        </Stack>

        {canReuse ? (
          <Stack spacing={2}>
            <TextField
              label="Amount Used"
              value={amountMLs}
              autoComplete="off"
              type="number"
              onChange={(e) => setAmountMLs(e.target.value)}
              required
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">ml</InputAdornment>
                ),
              }}
            />
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                borderColor: stockRemaining ? 'success.main' : 'warning.main',
                backgroundColor: 'background.default',
              }}
            >
              <Stack direction="row" spacing={1.5}>
                <MedicationLiquidIcon
                  color={stockRemaining ? 'success' : 'warning'}
                  sx={{ mt: 0.25 }}
                />
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2" fontWeight={800}>
                    Reusable Bottle
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    This medicine comes in a multi-dose container — for
                    example, a bottle of liquid paracetamol — where the same
                    bottle is used across several administrations.
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={stockRemaining}
                        onChange={(e) => setStockRemaining(e.target.checked)}
                      />
                    }
                    label="The bottle still has liquid left — keep it in the bag"
                  />
                  <Typography variant="caption" color="text.secondary">
                    {stockRemaining
                      ? inEpcr
                        ? 'The bottle stays in the bag. It is reserved for this record until the ePCR is submitted.'
                        : 'The bottle stays in the bag for future administrations.'
                      : 'Untick only if the bottle is now empty or is being discarded — it will be removed from stock.'}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>
          </Stack>
        ) : (
          <Box>
            <Typography variant="subtitle2" fontWeight={800} gutterBottom>
              Quantity Administered
            </Typography>

            <Stack direction="row" spacing={1.5} alignItems="center">
              <IconButton
                color="error"
                onClick={() => handleQTYchange('decrease')}
                disabled={selectedDrugsAdministered.length <= 0}
              >
                <IndeterminateCheckBoxIcon />
              </IconButton>

              <Typography variant="h5" fontWeight={900}>
                {selectedDrugsAdministered.length}
              </Typography>

              <IconButton
                color="success"
                onClick={() => handleQTYchange('increase')}
              >
                <AddBoxIcon />
              </IconButton>
            </Stack>
          </Box>
        )}

        <Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                value={totalDose}
                fullWidth
                onChange={(e) => setTotalDose(e.target.value)}
                type="number"
                label="Total Dose"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Select
                        variant="standard"
                        value={totalDoseType}
                        onChange={(e) => setTotalDoseType(e.target.value)}
                      >
                        {['mg', 'g', 'mcg', 'ml'].map((x) => (
                          <MenuItem key={x} value={x}>
                            {x}
                          </MenuItem>
                        ))}
                      </Select>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Route</InputLabel>
                <Select
                  value={route}
                  onChange={(e) => setRoute(e.target.value)}
                  label="Route"
                >
                  {[
                    'Intravenous',
                    'Intraosseous',
                    'Intramuscular',
                    'Subcutaneous',
                    'Oral',
                    'Buccal',
                    'Sublingual',
                    'Intranasal',
                    'Rectal',
                    'Nebulised',
                    'Inhaled',
                    'Topical',
                  ].map((x) => (
                    <MenuItem key={x} value={x}>
                      {x}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={7}>
              <EmployeeSelector
                fullWidth
                disableClearable
                label="Administered By"
                value={administeredBy}
                setSelfAsDefault
                onChange={(e, newValue) => setAdministeredBy(newValue)}
              />
            </Grid>

            <Grid item xs={12} md={5}>
              <AnDateTimePicker
                date={administeredTime}
                onChange={(newDate) => setAdministeredTime(newDate)}
                label="Administration Time"
                fullWidth
              />
            </Grid>
          </Grid>
        </Box>
      </Stack>
    </Paper>
  )
}

export default AdministrationDetailsCard
